import { GenericTableData } from "gen/model/genericTableData";
import { useCallback, useEffect, useRef } from "react";
import { useFormContext, UseFormReturn } from "react-hook-form";
import {
  iterateGenericTableCells,
  valueForCell,
} from "shared/components/table/data/internal/DataTableInternalUtils";
import { useMaybernTableUIContext } from "shared/components/table/maybern/MaybernTableUIContext";
import { tryGetAgGridApi } from "shared/components/table/maybern/MaybernTableUtils";
import { useDeepCompareEffect } from "use-deep-compare";

export type DataTableGetFormFn = () => UseFormReturn | null;

export const useDataTableForm = (tableData: GenericTableData) => {
  const { agGrid } = useMaybernTableUIContext();
  const form = useFormContext() as UseFormReturn | null;

  const formRef = useRef(form);
  formRef.current = form;

  const getForm = useCallback<DataTableGetFormFn>(
    () => formRef.current,
    [formRef],
  );

  const formValues = form?.watch();

  useEffect(() => {
    if (!form) {
      return;
    }

    // pre-register all form fields so any collapsed rows will still be included
    for (const cell of iterateGenericTableCells(tableData.rows)) {
      if (cell.form_field) {
        form.register(cell.form_field.name, { value: valueForCell(cell) });
      }
    }
    if (tableData.extra_form_data) {
      Object.entries(tableData.extra_form_data).forEach(([name, value]) => {
        form.register(name, { value });
      });
    }
  }, [form, tableData]);

  useDeepCompareEffect(() => {
    const api = tryGetAgGridApi(agGrid);
    if (!form || !api) {
      return;
    }
    api.refreshClientSideRowModel("aggregate");
    const totalNode = api.getPinnedBottomRow(0);
    if (totalNode) {
      api.refreshCells({ rowNodes: [totalNode] });
    }
  }, [agGrid, formValues, form]);

  return { getForm };
};
